// src/pages/accounts/CashBankBook.js
// D-289 Phase 2 — Cash / Bank Book: money that actually came in or went out.
//
// WHY THIS CHANGED
// The old book counted only rows whose status was "paid" and listed every
// ledger row (bills, allocations) as if money had moved. Since SEC-01 /
// D-289 a payment is a NEW type "payment" row and bills are never edited,
// so every new payment was missing and unpaid bills showed as "Out".
// Which rows are real money movement is now decided in ONE place —
// cashMovementOf() in src/utils/cashBook.js.
// D-296 (09 Oct 2026): each mode card = OPENING balance (Accounts → Opening
// Balances) + net flow since. In / Out / Net below stay money movement only,
// so In still equals Total Income.
// D-315 (24 Oct 2026): a payment reversal is a MINUS Out row in the payment's
// mode (cashBook.js). Sign and colour in the table now follow the row's real
// effect on the balance, so a reversal (and a negative income correction)
// shows correctly instead of always "+" for In and "-" for Out.

import { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { cashMovementOf, openingByMode } from "../../utils/cashBook";

const MODE_CARDS = [
  { key: "cash",   label: "Cash",                          color: "bg-green-600" },
  { key: "bank",   label: "Bank (Transfer / NEFT / RTGS)", color: "bg-blue-600" },
  { key: "upi",    label: "UPI",                           color: "bg-purple-600" },
  { key: "cheque", label: "Cheque",                        color: "bg-slate-600" },
];
const MODE_LABEL = { cash: "Cash", bank: "Bank", upi: "UPI", cheque: "Cheque", other: "Not recorded" };
const KIND_LABEL = { income: "Received", payment: "Paid", legacy_paid: "Paid (before khaata)", payment_reversal: "Payment reversed" };

const inr = (n) => `${Number(n) < 0 ? "- " : ""}₹${Math.abs(Number(n || 0)).toLocaleString("en-IN")}`;
const secondsOf = (ts) => (ts && ts.seconds) ? ts.seconds : 0;
// D-315: what a row does to the balance (+ adds money, - removes it).
const effectOf = (r) => (r.direction === "in" ? r.amount : -r.amount);

export default function CashBankBook() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || localStorage.getItem("company_id") || "";

  const [rows,       setRows]       = useState([]);
  const [opening,    setOpening]    = useState({ cash: 0, bank: 0, upi: 0, cheque: 0, other: 0 });
  const [modeFilter, setModeFilter] = useState("");   // "" | cash | bank | upi | cheque | other
  const [dirFilter,  setDirFilter]  = useState("");   // "" | in | out
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  useEffect(() => { load(); }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    if (!companyId) { setLoading(false); return; }
    setLoading(true);
    setError("");
    try {
      const snap = await getDocs(
        query(collection(db, "financial_transactions"), where("company_id", "==", companyId))
      );
      const all  = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setOpening(openingByMode(all));
      const list = [];
      all.forEach((row) => {
        const move = cashMovementOf(row);
        if (!move) return;
        // A legacy "paid" row is dated when it was paid, not when the bill came.
        const ts = move.kind === "legacy_paid"
          ? (row.paid_at || row.updated_at || row.created_at)
          : row.created_at;
        list.push({ ...move, id: row.id, at: secondsOf(ts), reference: row.reference_no || "" });
      });
      list.sort((a, b) => b.at - a.at);
      setRows(list);
    } catch (err) {
      console.error("CashBankBook error:", err);
      setError(err.message || "Failed to load Cash / Bank Book");
    } finally {
      setLoading(false);
    }
  };

  // Opening + net flow per mode over ALL rows — filters change only the table below.
  const balances = useMemo(() => {
    const b = { ...opening };
    rows.forEach((r) => { b[r.mode] += r.direction === "in" ? r.amount : -r.amount; });
    return b;
  }, [rows, opening]);

  const displayed = rows.filter((r) => {
    if (modeFilter && r.mode !== modeFilter) return false;
    if (dirFilter && r.direction !== dirFilter) return false;
    return true;
  });

  const totalIn  = displayed.filter((r) => r.direction === "in").reduce((s, r) => s + r.amount, 0);
  const totalOut = displayed.filter((r) => r.direction === "out").reduce((s, r) => s + r.amount, 0);

  const cards = balances.other !== 0
    ? [...MODE_CARDS, { key: "other", label: "Mode not recorded", color: "bg-amber-600" }]
    : MODE_CARDS;

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <h1 className="text-xl font-bold text-slate-800 mb-1">Cash / Bank Book</h1>
      <p className="text-xs text-slate-500 mb-5">
        Only money that actually came in or went out. Bills not yet paid are in Payables.
        Cards include the opening balance (Accounts → Opening Balances).
      </p>

      {/* NET FLOW PER MODE */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {cards.map(({ key, label, color }) => (
          <div key={key} className={`${color} text-white p-4 rounded-2xl shadow-sm`}>
            <p className="text-xs opacity-80">{label}</p>
            <p className={`text-xl font-black mt-0.5 ${balances[key] < 0 ? "text-red-200" : "text-white"}`}>
              {inr(balances[key])}
            </p>
            {opening[key] !== 0 && (
              <p className="text-[11px] opacity-80 mt-0.5">incl. opening {inr(opening[key])}</p>
            )}
          </div>
        ))}
      </div>

      {/* FILTERS */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <select value={modeFilter} onChange={(e) => setModeFilter(e.target.value)}
          className="border border-slate-200 p-2 rounded-xl text-sm bg-white outline-none">
          <option value="">All Modes</option>
          <option value="cash">Cash</option>
          <option value="bank">Bank</option>
          <option value="upi">UPI</option>
          <option value="cheque">Cheque</option>
          <option value="other">Not recorded</option>
        </select>

        <select value={dirFilter} onChange={(e) => setDirFilter(e.target.value)}
          className="border border-slate-200 p-2 rounded-xl text-sm bg-white outline-none">
          <option value="">Money In + Out</option>
          <option value="in">Money In only</option>
          <option value="out">Money Out only</option>
        </select>

        <div className="ml-auto flex gap-3 text-sm items-center">
          <span className="text-green-600 font-semibold">In: {inr(totalIn)}</span>
          <span className="text-red-600 font-semibold">Out: {inr(totalOut)}</span>
          <span className={`font-bold ${totalIn - totalOut >= 0 ? "text-blue-600" : "text-red-600"}`}>
            Net: {inr(totalIn - totalOut)}
          </span>
        </div>
      </div>

      {/* TABLE */}
      {loading ? (
        <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
      ) : error ? (
        <p className="text-red-600 text-sm text-center mt-10">{error}</p>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="p-3 text-left">Date</th>
                  <th className="p-3 text-left">Type</th>
                  <th className="p-3 text-left">Description</th>
                  <th className="p-3 text-left">Mode</th>
                  <th className="p-3 text-right">Amount</th>
                  <th className="p-3 text-left">Reference</th>
                </tr>
              </thead>
              <tbody>
                {displayed.length === 0 ? (
                  <tr><td colSpan={6} className="p-6 text-center text-gray-400">No money movement found.</td></tr>
                ) : displayed.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 text-slate-500 whitespace-nowrap">
                      {r.at
                        ? new Date(r.at * 1000).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                        : "-"}
                    </td>
                    <td className={`p-3 font-semibold ${effectOf(r) >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {KIND_LABEL[r.kind]}
                    </td>
                    <td className="p-3 text-slate-600 max-w-[260px] truncate">{r.label}</td>
                    <td className="p-3 text-slate-500">{MODE_LABEL[r.mode]}</td>
                    <td className={`p-3 text-right font-semibold ${effectOf(r) >= 0 ? "text-green-600" : "text-red-600"}`}>
                      {effectOf(r) >= 0 ? "+" : "-"}{inr(Math.abs(r.amount))}
                    </td>
                    <td className="p-3 text-slate-400 text-xs">{r.reference || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}